import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { logger } from "@fidget/common/utils";
import { useStore } from "../../../../../models";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import styles from "./billingSummary.css";
import { useAgentServices } from "../../../../../models/agentServices";

export interface Props {
  advisersCount: number;
  planPrice: number;
  planCurrency?: string;
  country?: string;
  vatId?: string;
}

export const BillingSummary = observer(
  ({ advisersCount, planPrice, planCurrency, country, vatId }: Props) => {
    const { spinnerService } = useAgentServices();
    const { paymentStore, currentUserStore, uiStore } = useStore();
    const [taxRate, setTaxRate] = useState<number>(0);
    const { nextInvoiceAmount, nextInvoiceDate } = currentUserStore!;
    const unitVat = taxRate ? Math.round(planPrice * taxRate * 100) / 100 : 0;
    const planPriceWithVat = planPrice + unitVat;
    const priceWithoutVat = planPrice * advisersCount;
    const rawVat = taxRate && priceWithoutVat * taxRate;
    const vat = rawVat ? Math.round(rawVat * 100) / 100 : 0;
    const nextBillingDate = new Date(nextInvoiceDate! * 1000);

    useEffect(() => {
      if (paymentStore && country && vatId) {
        paymentStore.getTaxRate(country, vatId).then(() => {
          const { taxRate: rate } = paymentStore;
          setTaxRate(rate || 0);
        });
      }
    }, []);

    useEffect(() => {
      try {
        currentUserStore!.updateInvoiceInfo();
      } catch (e) {
        logger.error({ message: "Couldn't update billing invoice", error: e });
        uiStore.addGenericErrorToast();
      }
    }, [advisersCount]);

    const formatPrice = (price: number) => intl.formatNumber(price, { minimumFractionDigits: 2 });

    const goToCustomerPortal = () => {
      spinnerService.goToCustomerPortal().then((response) => {
        const wind = window.open(response.url, "_blank");
        if (wind !== null) {
          wind.focus();
        } else {
          window.location.href = response.url;
        }
      });
    };

    return (
      <>
        <div className={styles.container}>
          <div className={styles.barItem}>
            <div className={styles.itemDescription}>
              <L10n id={"settings.user.advisers.count"} />
            </div>
            <div className={styles.itemValue}>
              <L10n id={"settings.user.advisers.count.value"} values={{ count: advisersCount }} />
            </div>
          </div>

          <div className={styles.barItem}>
            <div className={styles.itemDescription}>
              <L10n id={"settings.user.advisers.monthlyFee"} />
            </div>
            <div className={styles.itemValue}>
              {formatPrice(vat + priceWithoutVat)} {planCurrency}
            </div>
            <div className={styles.itemComment}>
              ({formatPrice(planPriceWithVat)} {planCurrency} /
              <L10n id={"settings.user.advisers.monthlyFeeComment"} />)
            </div>
          </div>

          <div className={styles.barItem}>
            <div className={styles.itemDescription}>
              <L10n id={"settings.user.advisers.nextInvoiceAmount"} />
            </div>
            <div className={styles.itemValueIconSpan}>
              <div className={styles.itemValue}>
                {formatPrice(nextInvoiceAmount! / 100)} {planCurrency}
              </div>
              <div className={styles.infoIconWrapper}>
                <TooltipComponent
                  text={intl.formatMessage("settings.user.advisers.nextInvoiceAmountInfo")}
                  positionPreset={"top"}
                  minWidth={"220px"}
                >
                  <div className={styles.infoIcon}>
                    <p>i</p>
                  </div>
                </TooltipComponent>
              </div>
            </div>
          </div>

          <div className={styles.barItem}>
            <div className={styles.itemDescription}>
              <L10n id={"settings.user.advisers.nextInvoiceDate"} />
            </div>
            <div className={styles.itemValue}>
              {nextBillingDate.getDate()}.{nextBillingDate.getMonth() + 1}.
              {nextBillingDate.getFullYear()}
            </div>
          </div>
        </div>
        <AgentButton onClick={goToCustomerPortal}>
          {intl.formatMessage("settings.admin.stripeCustomerPortal")}
        </AgentButton>
      </>
    );
  }
);
